/**
 * @file ResponsiveBackgrounds.ts
 * @role Resolves the configured background asset/bundle from the authoritative layout mode.
 * @owns The pure mapping shared by boot-time loading and scene presentation.
 * @reads LayoutContext and validated UiConfig only.
 * @neverDoes Load assets. Read window. Render.
 * @invariants 14, 15.
 */
import type { UiConfig } from '@core/config/UiConfig';
import type { LayoutContext } from '@core/layout/LayoutContext';
import { SlotLayoutModeResolver } from '@core/layout/SlotLayoutModeResolver';

export type ResponsiveBackgroundVariant =
  UiConfig['backgrounds']['byLayoutMode'][keyof UiConfig['backgrounds']['byLayoutMode']];

export function backgroundForLayout(
  layout: LayoutContext,
  uiConfig: Pick<UiConfig, 'backgrounds' | 'layoutPolicy'>,
): ResponsiveBackgroundVariant {
  const mode = SlotLayoutModeResolver.resolve(layout, uiConfig.layoutPolicy);
  return uiConfig.backgrounds.byLayoutMode[mode];
}

export function alternateBackgroundBundles(
  uiConfig: Pick<UiConfig, 'backgrounds'>,
  active: ResponsiveBackgroundVariant,
): ResponsiveBackgroundVariant['bundle'][] {
  return [
    ...new Set(
      Object.values(uiConfig.backgrounds.byLayoutMode)
        .filter(({ bundle }) => bundle !== active.bundle)
        .map(({ bundle }) => bundle),
    ),
  ];
}
